import { useCallback, useEffect, useRef, useState } from "react";
import { wsUrl } from "@/lib/api";
import type { LogEntry, LogFilters } from "@/lib/types";

type ConnectionState = "idle" | "connecting" | "open" | "closed";

type StreamResult = {
  logs: LogEntry[];
  state: ConnectionState;
  lastUpdate: Date | null;
  /** Anzeige eingefroren? Verbindung + Zähler laufen weiter. */
  paused: boolean;
  /** Anzahl der Log-Events, die seit der Pause eingegangen sind. */
  missed: number;
  pause: () => void;
  /** Hebt die Pause auf und holt per Re-Subscribe einen frischen Snapshot. */
  resume: () => void;
};

/**
 * Server-Protokoll (siehe src/frontend/websocket.rs):
 * Client → { type: "subscribe", filters: { level, levelMode, client, service, host, trace_id, search, limit } }
 * Server → { type: "snapshot", logs: LogEntry[] }  komplette Liste (nach Subscribe)
 *        → { type: "append",   logs: LogEntry[] }  nur neue Zeilen (neueste zuerst)
 * Legacy-Server (< Delta-Protokoll) schicken die Liste als nacktes Array.
 */
function toWireFilters(f: LogFilters) {
  return {
    level: f.level,
    level_mode: f.levelMode,
    client: f.client,
    service: f.service,
    host: f.host,
    trace_id: f.traceId,
    search: f.search,
    limit: f.limit ?? 100,
  };
}

/**
 * Delta einarbeiten: neue Zeilen (neueste zuerst) vor die Liste, Duplikate
 * per ID raus (Server kann sich bei parallelen Fetches überlappen), auf das
 * Anzeige-Limit kappen.
 */
function mergeAppend(incoming: LogEntry[], prev: LogEntry[], limit: number): LogEntry[] {
  if (incoming.length === 0) return prev;
  const seen = new Set(incoming.map((l) => l.id).filter((id) => id != null));
  const rest = prev.filter((l) => l.id == null || !seen.has(l.id));
  return [...incoming, ...rest].slice(0, limit);
}

export function useLogStream(filters: LogFilters, enabled: boolean): StreamResult {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [state, setState] = useState<ConnectionState>("idle");
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [paused, setPaused] = useState(false);
  const [missed, setMissed] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<number | null>(null);
  const filtersRef = useRef(filters);
  // Ref statt State im onmessage-Handler — der Handler lebt über die gesamte
  // Verbindung und darf nicht an einen alten paused-Wert gebunden sein.
  const pausedRef = useRef(false);

  filtersRef.current = filters;

  useEffect(() => {
    if (!enabled) {
      wsRef.current?.close();
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const connect = () => {
      if (cancelled) return;
      setState("connecting");
      const ws = new WebSocket(wsUrl("/api/ws"));
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        attempts = 0;
        setState("open");
        ws.send(JSON.stringify({ type: "subscribe", filters: toWireFilters(filtersRef.current) }));
      };

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          if (Array.isArray(parsed)) {
            // Legacy: komplette Liste als nacktes Array.
            if (pausedRef.current) return;
            setLogs(parsed as LogEntry[]);
            setLastUpdate(new Date());
            return;
          }
          if (!parsed || !Array.isArray(parsed.logs)) return;
          const incoming = parsed.logs as LogEntry[];
          if (pausedRef.current) {
            // Anzeige eingefroren: nichts einarbeiten, nur mitzählen —
            // resume() holt sich per Re-Subscribe einen frischen Snapshot.
            if (parsed.type === "append") setMissed((n) => n + incoming.length);
            return;
          }
          if (parsed.type === "snapshot") {
            setLogs(incoming);
          } else if (parsed.type === "append") {
            const limit = filtersRef.current.limit ?? 100;
            setLogs((prev) => mergeAppend(incoming, prev, limit));
          } else {
            return;
          }
          setLastUpdate(new Date());
        } catch {
          // ignore
        }
      };

      ws.onclose = () => {
        if (cancelled) return;
        setState("closed");
        wsRef.current = null;
        const delay = Math.min(30_000, 1000 * 2 ** attempts++);
        reconnectRef.current = window.setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectRef.current) window.clearTimeout(reconnectRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [enabled]);

  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "subscribe", filters: toWireFilters(filters) }));
  }, [
    filters.level,
    filters.levelMode,
    filters.client,
    filters.service,
    filters.host,
    filters.traceId,
    filters.search,
    filters.limit,
  ]);

  const pause = useCallback(() => {
    pausedRef.current = true;
    setPaused(true);
    setMissed(0);
  }, []);

  const resume = useCallback(() => {
    pausedRef.current = false;
    setPaused(false);
    setMissed(0);
    // Frischer Snapshot statt Delta-Buchhaltung über die Pause hinweg.
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "subscribe", filters: toWireFilters(filtersRef.current) }));
    }
  }, []);

  return { logs, state, lastUpdate, paused, missed, pause, resume };
}
